import React from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import Box from '@material-ui/core/Box';
import { DropzoneArea } from 'material-ui-dropzone';
import { makeStyles } from '@material-ui/styles';
import InsertPhotoIcon from '@material-ui/icons/InsertPhoto';
import AttachFileIcon from '@material-ui/icons/AttachFile';

// styles
import useStyles from '../styles';

// images
import photoRequirementsImage from '../../../images/photo-new-requirements.jpg';

// components
import { Typography } from '../../../components/Wrappers/Wrappers';
import Notification from '../../../components/Notification/Notification';

// context
import { useOrderDispatch, useOrderState, actions } from '../../../context/OrderContext';

// form attributes
import { impressionMethod, transferMethod } from '../helpers/form';

// compression
import { compressIfNeeded } from '../../../utils/scanFileCompressor';

// Custom styles for dropzone spacing
const useDropzoneStyles = makeStyles({
    dropzoneContainer: {
        marginBottom: 8,
        '& .MuiDropzoneArea-root': {
            padding: '12px 24px !important', // Reduced padding
        },
    },
    reducedHeightDropzone: {
        minHeight: '100px !important', // Further reduced height
        '& .MuiDropzoneArea-textContainer': {
            minHeight: '50px !important',
        },
    },
});

// iOS delivers HEIC/HEIF photos, but Chrome/Firefox/Edge can't decode them for an <img> preview
// (only Safari can), so the dropzone's default preview renders a broken-image glyph. Show a
// generic photo icon for those files instead, and keep the real thumbnail for formats browsers
// can render.
const isHeicFile = (file) => /\.hei[cf]$/i.test(file.name);

const getPreviewIcon = (fileObject, classes) => {
    const { file } = fileObject;
    if (isHeicFile(file)) {
        return <InsertPhotoIcon className={classes.image} />;
    }
    if (file.type.split('/')[0] === 'image') {
        return <img className={classes.image} role="presentation" src={fileObject.data} alt={file.name} />;
    }
    return <AttachFileIcon className={classes.image} />;
};

const AttachmentRequirements = ({ isImpressionMethod, isTransferMethod }) => {
    const classes = useStyles();
    return (
        <>
            <Typography className={classes.marginText}>
                <span className={classes.boldColoredText}>Please upload the following:</span>
            </Typography>

            {isImpressionMethod ? (
                <>
                    <Typography className={classes.marginText} weight="semi-bold">
                        - Patient's bite photos showing facial, left and right views
                    </Typography>
                    <Typography weight="semi-bold">
                        - Patient photos, OPG and lat ceph (up to 12 photos maximum)
                    </Typography>
                    <Typography className={classes.marginText} style={{ fontStyle: 'italic', color: '#666' }}>
                        Note: 3D scan files will be uploaded by Smile Academy after receiving your scans.
                    </Typography>
                </>
            ) : isTransferMethod ? (
                <>
                    <Typography className={classes.marginText} weight="semi-bold">
                        - Patient photos, OPG and lat ceph (up to 12 photos maximum)
                    </Typography>
                    <Typography className={classes.marginText} style={{ fontStyle: 'italic', color: '#666' }}>
                        Note: 3D scan files will be uploaded by Smile Academy after receiving your scans.
                    </Typography>
                </>
            ) : (
                <>
                    <Typography className={classes.marginText} weight="semi-bold">
                        - Patient scans: upper & lower arches (STL, PLY or OBJ files)
                    </Typography>
                    <Typography className={classes.marginText} weight="semi-bold">
                        - Patient photos, OPG and lat ceph (up to 12 photos maximum)
                    </Typography>
                </>
            )}

            <div className={classes.photoReqWrap}>
                <img src={photoRequirementsImage} alt="requirements" height="100%" />
            </div>
        </>
    );
};

const FileUpload = ({ newOrder }) => {
    const classes = useStyles();
    const dropzoneClasses = useDropzoneStyles();

    const orderDispatch = useOrderDispatch();
    const orderState = useOrderState();

    // Flag to prevent upload when triggered by file deletion
    const [isDeletingInProgress, setIsDeletingInProgress] = React.useState(false);
    // Progress tracking for uploads
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const [totalFilesToUpload, setTotalFilesToUpload] = React.useState(0);

    const isUploadingFile = orderState.photosUploadLoading;
    const hasUploadedFile = orderState.photosUpload;
    const isDeletingFile = orderState.photosDeleteLoading;
    const photosDeleteError = orderState.photosDeleteError;

    const handleUploadFiles = async (files) => {
        // Prevent upload if it's triggered by a file deletion
        if (isDeletingInProgress || !files.length) return;

        // Reset progress tracking
        setUploadProgress(0);
        setTotalFilesToUpload(files.length);

        // Progress tracking function
        const onUploadProgress = (progressInfo) => {
            setUploadProgress(progressInfo.percentage);
        };

        try {
            // Start the actual upload with progress tracking
            await actions.doUploadPatientPhotos(files, newOrder, onUploadProgress)(orderDispatch);

            // Upload completed successfully - show 100% briefly
            setUploadProgress(100);

            // Reset states after a short delay
            setTimeout(() => {
                setUploadProgress(0);
                setTotalFilesToUpload(0);
            }, 500);
        } catch (error) {
            // Reset progress on error
            setUploadProgress(0);
            setTotalFilesToUpload(0);
        }
    };
    const handleDeleteFile = async (file) => {
        // Set flag to prevent upload from being triggered
        setIsDeletingInProgress(true);

        try {
            // Extract the file information that we need for deletion
            // The DropzoneArea onDelete callback provides the file object
            // We need to find the corresponding uploaded file from our state

            if (hasUploadedFile?.uploadedFiles) {
                // Find the file in our uploaded files by matching original name or other criteria
                const fileToDelete = hasUploadedFile.uploadedFiles.find(
                    (uploadedFile) =>
                        uploadedFile.originalName === file.name ||
                        uploadedFile.originalName === file.file?.name ||
                        uploadedFile.filename === file.name
                );

                if (fileToDelete) {
                    // Create a getState function that returns the current order state
                    const getState = () => ({ photosUpload: hasUploadedFile });
                    await actions.doDeletePatientPhoto(fileToDelete, newOrder)(orderDispatch, getState);
                } else {
                    // Fallback: just remove from local state if we can't find the file
                    actions.doDeleteUploadedFile()(orderDispatch);
                }
            } else {
                // Fallback: just remove from local state
                actions.doDeleteUploadedFile()(orderDispatch);
            }
        } finally {
            // Reset the flag after a short delay to allow the onChange to be ignored
            setTimeout(() => {
                setIsDeletingInProgress(false);
            }, 100);
        }
    };

    return (
        <>
            {/* Fixed-height feedback container to prevent UI jumping */}
            <Box
                style={{
                    minHeight: '40px',
                    marginBottom: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                }}
            >
                {/* Success notification for uploads */}
                {!isUploadingFile && hasUploadedFile && !isDeletingFile && (
                    <Notification
                        className={classes.notificationItem}
                        type="shipped"
                        message={`Successfully uploaded ${
                            hasUploadedFile.totalFiles || hasUploadedFile.uploadedFiles?.length || 1
                        } file(s)`}
                        color="success"
                    />
                )}

                {/* Error notification for file uploads */}
                {orderState.photosUploadError && (
                    <Notification
                        className={classes.notificationItem}
                        type="report"
                        message="Failed to upload file. Please try again."
                        color="error"
                    />
                )}

                {/* Error notification for file deletion */}
                {photosDeleteError && (
                    <Notification
                        className={classes.notificationItem}
                        type="report"
                        message="Failed to delete file. Please try again."
                        color="error"
                    />
                )}

                {/* Upload progress indicator */}
                {isUploadingFile && (
                    <Box mt={1} mr={2} width="100%">
                        <LinearProgress
                            color="secondary"
                            variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
                            value={uploadProgress}
                        />
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ marginTop: 10, marginBottom: 10 }}
                        >
                            <Typography size={'sm'} color="secondary">
                                {uploadProgress > 0
                                    ? `Uploading ${totalFilesToUpload} files... (${Math.round(uploadProgress)}%)`
                                    : 'Uploading files...'}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            <Box className={dropzoneClasses.dropzoneContainer}>
                <DropzoneArea
                    acceptedFiles={['.jpeg', '.jpg', '.png', '.heic']}
                    filesLimit={12}
                    maxFileSize={50000000} // 50Mb file size limit
                    dropzoneText={"Drag and drop patient's photos here or click to upload"}
                    showFileNames={true}
                    onChange={handleUploadFiles}
                    onDelete={handleDeleteFile}
                    dropzoneClass={dropzoneClasses.reducedHeightDropzone}
                    disabled={isUploadingFile || isDeletingFile}
                    getPreviewIcon={getPreviewIcon}
                />
            </Box>

            <Typography className={classes.noteText} style={{ marginBottom: 25, color: '#666', fontStyle: 'italic' }}>
                Accepted file formats: JPEG, JPG, PNG, HEIC (up to 12 files, 50MB max per file)
            </Typography>
        </>
    );
};

const AttachmentNote = () => (
    <div style={{ marginBottom: 20, padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <Typography weight="bold" style={{ marginBottom: 6 }}>
            Notes:
        </Typography>
        <Typography style={{ marginBottom: 6, fontStyle: 'italic' }}>
            • OPG and lateral cephalometric photos are optional but recommended
        </Typography>
        <Typography style={{ marginBottom: 6, fontStyle: 'italic' }}>
            • If you would like to share a CBCT scan, please send to <br />
            info@smile-academy.com.au using WeTransfer or Dropbox
        </Typography>
        <Typography style={{ fontStyle: 'italic' }}>
            • With a CBCT scan, we will be able to visualise the patient's jaws and roots on the treatment plan
        </Typography>
    </div>
);

// 3D File Upload Component for Single File (STL/PLY/OBJ)
const ThreeDFileUpload = ({ newOrder, fileType, label }) => {
    const classes = useStyles();
    const dropzoneClasses = useDropzoneStyles();

    const orderDispatch = useOrderDispatch();
    const orderState = useOrderState();

    // Progress tracking for uploads
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const [isDeletingInProgress, setIsDeletingInProgress] = React.useState(false);
    const [isCompressing, setIsCompressing] = React.useState(false);
    const [compressionError, setCompressionError] = React.useState(null);

    // Get state values based on file type
    const stateKey = fileType.toLowerCase() + 'ArchUpload';
    const loadingKey = fileType.toLowerCase() + 'ArchUploadLoading';
    const errorKey = fileType.toLowerCase() + 'ArchUploadError';

    const isUploadingFile = orderState[loadingKey];
    const hasUploadedFile = orderState[stateKey];
    const uploadError = orderState[errorKey];

    const handleUploadFiles = async (files) => {
        // Prevent upload if it's triggered by a file deletion or no files
        if (isDeletingInProgress || !files.length) return;

        // Validate file extensions manually (important for iOS compatibility)
        const allowedExtensions = ['.stl', '.ply', '.obj'];
        const invalidFiles = files.filter((file) => {
            const fileName = file.name.toLowerCase();
            return !allowedExtensions.some((ext) => fileName.endsWith(ext));
        });

        if (invalidFiles.length > 0) {
            orderDispatch({
                type: `ORDER_${fileType.toUpperCase()}_UPLOAD_ERROR`,
                payload: `Invalid file type. Only STL, PLY, and OBJ files are allowed.`,
            });
            return;
        }

        setCompressionError(null);
        setUploadProgress(0);

        // Compress files >= 45 MB before uploading
        const filesToUpload = [];
        setIsCompressing(true);
        try {
            for (const file of files) {
                const { file: processedFile, tooLarge, reason } = await compressIfNeeded(file);
                if (tooLarge) {
                    if (reason === 'format_unsupported') {
                        const ext = file.name.split('.').pop().toUpperCase();
                        setCompressionError(
                            `This ${ext} file is too large to upload. Please reduce the scan resolution before exporting, or export as STL format instead.`
                        );
                    } else {
                        setCompressionError(
                            `The ${label.toLowerCase()} file is too large to upload. Please reduce the scan resolution before exporting.`
                        );
                    }
                    return;
                }
                filesToUpload.push(processedFile);
            }
        } catch (err) {
            console.error('Compression failed, uploading original file:', err);
            filesToUpload.push(...files);
        } finally {
            setIsCompressing(false);
        }

        // Progress tracking function
        const onUploadProgress = (progressInfo) => {
            setUploadProgress(progressInfo.percentage);
        };

        try {
            await actions[`doUpload${fileType}Arch`](filesToUpload, newOrder, onUploadProgress)(orderDispatch);

            setUploadProgress(100);
            setTimeout(() => {
                setUploadProgress(0);
            }, 500);
        } catch (error) {
            setUploadProgress(0);
        }
    };

    const handleDeleteFile = async (file) => {
        setIsDeletingInProgress(true);
        setCompressionError(null);

        try {
            if (hasUploadedFile?.uploadedFiles) {
                // Find the file in our uploaded files
                const fileToDelete = hasUploadedFile.uploadedFiles.find(
                    (uploadedFile) => uploadedFile.originalName === file.name || uploadedFile.filename === file.name
                );

                if (fileToDelete) {
                    const getState = () => ({ [stateKey]: hasUploadedFile });
                    await actions[`doDelete${fileType}Arch`](fileToDelete, newOrder)(orderDispatch, getState);
                } else {
                    // Fallback: just remove from local state
                    orderDispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_DELETE` });
                }
            } else {
                // Fallback: just remove from local state
                orderDispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_DELETE` });
            }
        } finally {
            // Reset the flag after a short delay
            setTimeout(() => {
                setIsDeletingInProgress(false);
            }, 100);
        }
    };

    return (
        <div style={{ marginBottom: 20 }}>
            <Typography variant="h6" weight="medium" style={{ marginBottom: 12 }}>
                {label} *
            </Typography>

            {/* Fixed-height feedback container */}
            <Box
                style={{
                    minHeight: '40px',
                    marginBottom: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                }}
            >
                {/* Success notification */}
                {!isUploadingFile && !isCompressing && hasUploadedFile && (
                    <Notification
                        className={classes.notificationItem}
                        type="shipped"
                        message={`Successfully uploaded ${label.toLowerCase()}`}
                        color="success"
                    />
                )}

                {/* File optimisation in progress (ASCII STL → binary conversion) */}
                {isCompressing && (
                    <Box mt={1} mr={2} width="100%">
                        <LinearProgress color="secondary" variant="indeterminate" />
                        <Box style={{ marginTop: 10, marginBottom: 10 }}>
                            <Typography size={'sm'} color="secondary">
                                Optimising {label.toLowerCase()} for upload...
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* File too large after compression */}
                {compressionError && (
                    <Notification
                        className={classes.notificationItem}
                        type="report"
                        message={compressionError}
                        color="error"
                    />
                )}

                {/* Upload error */}
                {uploadError && !compressionError && (
                    <Notification
                        className={classes.notificationItem}
                        type="report"
                        message={`Failed to upload ${label.toLowerCase()}. Please try again.`}
                        color="error"
                    />
                )}

                {/* Upload progress indicator */}
                {isUploadingFile && (
                    <Box mt={1} mr={2} width="100%">
                        <LinearProgress
                            color="secondary"
                            variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
                            value={uploadProgress}
                        />
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ marginTop: 10, marginBottom: 10 }}
                        >
                            <Typography size={'sm'} color="secondary">
                                {uploadProgress > 0
                                    ? `Uploading ${label.toLowerCase()}... (${Math.round(uploadProgress)}%)`
                                    : `Uploading ${label.toLowerCase()}...`}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            <Box className={dropzoneClasses.dropzoneContainer}>
                <DropzoneArea
                    acceptedFiles={[
                        // File extensions for desktop browsers
                        '.stl',
                        '.ply',
                        '.obj',
                        // MIME types for better browser compatibility
                        'model/stl',
                        'model/x.stl-ascii',
                        'model/x.stl-binary',
                        'application/vnd.ms-pki.stl',
                        'application/ply',
                        'application/object',
                        // iOS Safari requires this for unknown binary formats
                        // Client-side validation in handleUploadFiles ensures only .stl/.ply/.obj are accepted
                        'application/octet-stream',
                    ]}
                    filesLimit={1}
                    maxFileSize={100000000} // 100MB file size limit
                    dropzoneText={`Drag and drop ${label.toLowerCase()} file here or click to upload`}
                    showFileNames={true}
                    onChange={handleUploadFiles}
                    onDelete={handleDeleteFile}
                    disabled={isUploadingFile || isCompressing}
                    dropzoneClass={dropzoneClasses.reducedHeightDropzone}
                    showPreviews={false} // Disable preview for 3D files
                />
            </Box>

            <Typography className={classes.noteText} style={{ marginBottom: 15, color: '#666', fontStyle: 'italic' }}>
                Accepted file formats: STL, PLY, OBJ (1 file, 100MB max)
                <br />
                <strong>This file is required to proceed to the next step.</strong>
            </Typography>
        </div>
    );
};

const FileAttachment = ({ newOrder }) => {
    const isImpressionMethod = newOrder.method === impressionMethod;
    const isTransferMethod = newOrder.method === transferMethod;
    // For both impression and transfer methods, 3D files are not required at order creation
    const requiresFileUpload = !isImpressionMethod && !isTransferMethod;

    return (
        <>
            <AttachmentRequirements isImpressionMethod={isImpressionMethod} isTransferMethod={isTransferMethod} />
            <AttachmentNote />

            {/* 3D File Upload Components - Only show for scan method (direct upload) */}
            {requiresFileUpload && (
                <div style={{ marginTop: 15, borderTop: '1px solid #eee', paddingTop: 20 }}>
                    <Typography variant="h6" weight="bold" style={{ marginBottom: 15, color: '#333' }}>
                        3D Scan Files (Required)
                    </Typography>
                    <ThreeDFileUpload newOrder={newOrder} fileType="Maxillary" label="Upper Arch" />
                    <ThreeDFileUpload newOrder={newOrder} fileType="Mandibular" label="Lower Arch" />
                </div>
            )}

            {/* Patient's Photos */}
            <div style={{ marginTop: 5, borderTop: '1px solid #eee', paddingTop: 20 }}>
                <Typography variant="h6" weight="bold" style={{ marginBottom: 15, color: '#333' }}>
                    Patient's Photos
                </Typography>
                <FileUpload newOrder={newOrder} />
            </div>
        </>
    );
};

export default FileAttachment;
